import React, { useMemo, useState, useEffect, useRef } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { getPendingPayments } from "../../apis/pendingPayments";
import { getCompletedPayments } from "../../apis/completedPayments";
import { format, subDays } from "date-fns";
import { DatePicker, Space } from "antd";
import dayjs from "dayjs";
import PaymentModal from "../../components/PaymentModal";
import ReceiptModal from "../../components/receipts/ReceiptModal";

const { RangePicker } = DatePicker;

type BillingType = "opd" | "ipd" | "lab" | "pharmacy";
type BillingTab = "pending" | "completed";

type PaymentItem = {
  id: string;
  patientName: string;
  patientId: number | null;
  sourceId: number;
  invoiceId: number | null;
  typeLabel: string;
  typeCode: "opd" | "ipd" | "lab_order" | "pharmacy";
  reference: string;
  amount: number;
  date: string | null;
};

type PendingSummary = {
  totalPending: number;
  opdPending: number;
  ipdPending: number;
  labPending: number;
  pharmacyPending: number;
};

type CompletedSummary = {
  totalCompleted: number;
  opdCompleted: number;
  ipdCompleted: number;
  labCompleted: number;
  pharmacyCompleted: number;
};

const defaultDateFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
const defaultDateTo = format(new Date(), "yyyy-MM-dd");

const BillingList: React.FC = () => {
  const [activeTab, setActiveTab] = useState<BillingTab>("pending");
  const [activeType, setActiveType] = useState<BillingType | "all">("all");
  const [searchText, setSearchText] = useState("");
  const [pendingPayments, setPendingPayments] = useState<PaymentItem[]>([]);
  const [completedPayments, setCompletedPayments] = useState<PaymentItem[]>([]);
  const [pendingSummary, setPendingSummary] = useState<PendingSummary>({
    totalPending: 0,
    opdPending: 0,
    ipdPending: 0,
    labPending: 0,
    pharmacyPending: 0,
  });
  const [completedSummary, setCompletedSummary] = useState<CompletedSummary>({
    totalCompleted: 0,
    opdCompleted: 0,
    ipdCompleted: 0,
    labCompleted: 0,
    pharmacyCompleted: 0,
  });
  const [completedDateRange, setCompletedDateRange] = useState<[string, string]>([defaultDateFrom, defaultDateTo]);
  const [completedPagination, setCompletedPagination] = useState({ page: 1, perPage: 15, total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isCompletedLoading, setIsCompletedLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentItem | null>(null);
  const [selectedBillPayment, setSelectedBillPayment] = useState<PaymentItem | null>(null);
  const requestIdRef = useRef(0);
  const completedRequestIdRef = useRef(0);

  const getPaymentModalSourceType = (
    typeCode: PaymentItem["typeCode"]
  ): "visit" | "pathology" | "pharmacy" => {
    if (typeCode === "lab_order") return "pathology";
    if (typeCode === "pharmacy") return "pharmacy";
    return "visit";
  };

  const pendingTotals = useMemo(() => ({
    all: pendingSummary.totalPending,
    opd: pendingSummary.opdPending,
    ipd: pendingSummary.ipdPending,
    lab: pendingSummary.labPending,
    pharmacy: pendingSummary.pharmacyPending,
  }), [pendingSummary]);

  const typeLabels: Record<BillingType | "all", string> = {
    all: "All",
    opd: "OPD Visits",
    ipd: "IPD Visits",
    lab: "Lab Orders",
    pharmacy: "Pharmacy",
  };

  const mapTypeToApi = (type: BillingType) => {
    if (type === "lab") return "lab_order";
    return type;
  };

  const formatType = (typeCode: PaymentItem["typeCode"]) => {
    switch (typeCode) {
      case "opd":
        return "OPD Visit";
      case "ipd":
        return "IPD Visit";
      case "lab_order":
        return "Lab Order";
      case "pharmacy":
        return "Pharmacy";
      default:
        return typeCode;
    }
  };

  const filteredPendingPayments = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) return pendingPayments;
    return pendingPayments.filter(
      (payment) =>
        payment.patientName.toLowerCase().includes(normalizedSearch) ||
        payment.reference.toLowerCase().includes(normalizedSearch)
    );
  }, [searchText, pendingPayments]);

  const fetchPendingPayments = () => {
    const params: any = {};
    if (activeType !== "all") params.type = mapTypeToApi(activeType);
    if (searchText.trim()) params.search = searchText.trim();

    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);
    setPendingPayments([]);

    return getPendingPayments(params)
      .then((res: any) => {
        if (currentRequestId !== requestIdRef.current) return;
        const data = res?.data ?? res;
        const items = data?.data ?? [];
        const mappedItems: PaymentItem[] = items.map((item: any) => {
          const typeCode = item.typeCode ?? item.type_code ?? "opd";
          const reference = item.referenceNumber ?? item.reference_number ?? "-";
          const sourceId = item.sourceId ?? item.source_id;
          return {
            id: sourceId != null ? `${typeCode}-${sourceId}` : `${typeCode}-${reference}`,
            patientName: item.patientName ?? item.patient_name ?? "",
            patientId: item.patientId ?? item.patient_id ?? null,
            sourceId: Number(sourceId ?? 0),
            invoiceId: item.invoiceId ?? item.invoice_id ?? null,
            typeLabel: item.type ?? "",
            typeCode,
            reference,
            amount: Number(item.amount ?? 0),
            date: item.dueDate ?? item.due_date ?? item.date ?? null,
          };
        });
        setPendingPayments(mappedItems);
        setPendingSummary({
          totalPending: Number(
            data?.summary?.totalPending ?? data?.summary?.total_pending ?? 0
          ),
          opdPending: Number(
            data?.summary?.opdPendingAmount ??
              data?.summary?.opd_pending_amount ??
              0
          ),
          ipdPending: Number(
            data?.summary?.ipdPendingAmount ??
              data?.summary?.ipd_pending_amount ??
              0
          ),
          labPending: Number(
            data?.summary?.labPendingAmount ??
              data?.summary?.lab_pending_amount ??
              0
          ),
          pharmacyPending: Number(
            data?.summary?.pharmacyPendingAmount ??
              data?.summary?.pharmacy_pending_amount ??
              0
          ),
        });
      })
      .catch((error) => {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("Failed to load pending payments", error);
      })
      .finally(() => {
        if (currentRequestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (activeTab !== "pending") return;
    fetchPendingPayments();
  }, [activeTab, activeType, searchText]);

  const fetchCompletedPayments = (page = 1) => {
    const currentRequestId = ++completedRequestIdRef.current;
    setIsCompletedLoading(true);
    getCompletedPayments({
      dateFrom: completedDateRange[0],
      dateTo: completedDateRange[1],
      perPage: completedPagination.perPage,
      page,
    })
      .then((res: any) => {
        if (currentRequestId !== completedRequestIdRef.current) return;
        const data = res?.data ?? res;
        const items = data?.data ?? [];
        const meta = data?.meta ?? {};
        const mappedItems: PaymentItem[] = items.map((item: any) => {
          const typeCode = item.typeCode ?? item.type_code ?? "opd";
          const reference = item.referenceNumber ?? item.reference_number ?? "-";
          const sourceId = item.sourceId ?? item.source_id ?? 0;
          return {
            id: `${typeCode}-${sourceId}`,
            patientName: item.patientName ?? item.patient_name ?? "",
            patientId: item.patientId ?? item.patient_id ?? null,
            sourceId: Number(sourceId),
            invoiceId: item.invoiceId ?? item.invoice_id ?? null,
            typeLabel: item.type ?? "",
            typeCode,
            reference,
            amount: Number(item.amount ?? 0),
            date: item.date ?? null,
          };
        });
        setCompletedPayments(mappedItems);
        setCompletedPagination((prev) => ({
          ...prev,
          page,
          total: meta.total ?? 0,
        }));
        setCompletedSummary({
          totalCompleted: Number(
            data?.summary?.totalCompleted ?? data?.summary?.total_completed ?? 0
          ),
          opdCompleted: Number(
            data?.summary?.opdCompletedAmount ??
              data?.summary?.opd_completed_amount ??
              0
          ),
          ipdCompleted: Number(
            data?.summary?.ipdCompletedAmount ??
              data?.summary?.ipd_completed_amount ??
              0
          ),
          labCompleted: Number(
            data?.summary?.labCompletedAmount ??
              data?.summary?.lab_completed_amount ??
              0
          ),
          pharmacyCompleted: Number(
            data?.summary?.pharmacyCompletedAmount ??
              data?.summary?.pharmacy_completed_amount ??
              0
          ),
        });
      })
      .catch((error) => {
        if (currentRequestId !== completedRequestIdRef.current) return;
        console.error("Failed to load completed payments", error);
      })
      .finally(() => {
        if (currentRequestId !== completedRequestIdRef.current) return;
        setIsCompletedLoading(false);
      });
  };

  useEffect(() => {
    if (activeTab !== "completed") return;
    fetchCompletedPayments(1);
  }, [activeTab, completedDateRange[0], completedDateRange[1]]);

  const displayPayments = activeTab === "pending" ? filteredPendingPayments : completedPayments;
  const displayLoading = activeTab === "pending" ? isLoading : isCompletedLoading;
  const summary = activeTab === "pending" ? pendingTotals : {
    all: completedSummary.totalCompleted,
    opd: completedSummary.opdCompleted,
    ipd: completedSummary.ipdCompleted,
    lab: completedSummary.labCompleted,
    pharmacy: completedSummary.pharmacyCompleted,
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Billing
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {activeTab === "pending"
              ? "Pending payments across OPD, IPD, Lab, and Pharmacy."
              : "Completed payments within the selected date range."}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-4 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab("pending")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
            activeTab === "pending"
              ? "border-primary text-primary"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Pending payments
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("completed")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
            activeTab === "completed"
              ? "border-primary text-primary"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Completed payments
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 mt-6 sm:grid-cols-2 lg:grid-cols-5">
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">
            Total {activeTab === "pending" ? "Pending" : "Completed"}
          </p>
          <p className="mt-2 text-2xl font-bold text-gray-900">₹{summary.all}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">OPD {activeTab === "pending" ? "Pending" : "Completed"}</p>
          <p className="mt-2 text-2xl font-bold text-blue-600">₹{summary.opd}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">IPD {activeTab === "pending" ? "Pending" : "Completed"}</p>
          <p className="mt-2 text-2xl font-bold text-indigo-600">₹{summary.ipd}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">Lab {activeTab === "pending" ? "Pending" : "Completed"}</p>
          <p className="mt-2 text-2xl font-bold text-amber-600">₹{summary.lab}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">Pharmacy {activeTab === "pending" ? "Pending" : "Completed"}</p>
          <p className="mt-2 text-2xl font-bold text-emerald-600">₹{summary.pharmacy}</p>
        </div>
      </div>

      {activeTab === "pending" ? (
      <div className="flex flex-col gap-4 mt-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {(["all", "opd", "ipd", "lab", "pharmacy"] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setActiveType(type)}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${activeType === type
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              {typeLabels[type]}
            </button>
          ))}
        </div>
        <div className="relative w-full max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search by patient or reference"
            className="py-2 pr-3 pl-10 w-full text-sm rounded-md border border-gray-300 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>
      ) : (
        <div className="flex flex-col gap-4 mt-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Date range:</span>
            <Space>
              <RangePicker
                value={[
                  completedDateRange[0] ? dayjs(completedDateRange[0]) : null,
                  completedDateRange[1] ? dayjs(completedDateRange[1]) : null,
                ] as any}
                onChange={(dates, dateStrings) => {
                  if (dates?.[0] && dates?.[1]) {
                    setCompletedDateRange([
                      dayjs(dates[0]).format("YYYY-MM-DD"),
                      dayjs(dates[1]).format("YYYY-MM-DD"),
                    ]);
                  }
                }}
                format="DD MMM YYYY"
              />
            </Space>
          </div>
        </div>
      )}

      <div className="flow-root mt-6">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Patient
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Type
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Reference
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {activeTab === "pending" ? "Due Date" : "Date"}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayPayments.length > 0 ? (
                    displayPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">
                          {payment.patientName}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {payment.typeLabel || formatType(payment.typeCode)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {payment.reference}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {payment.date ? format(new Date(payment.date), "dd MMM yyyy") : "-"}
                        </td>
                        <td className="px-3 py-4 text-sm font-semibold text-gray-900">
                          ₹{payment.amount}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {activeTab === "pending" ? (
                            (() => {
                              const typeLabel =
                                payment.typeLabel || formatType(payment.typeCode);
                              const isIpdPharmacyOrLab =
                                (payment.typeCode === "lab_order" ||
                                  payment.typeCode === "pharmacy") &&
                                /\(ipd\)/i.test(typeLabel);
                              return (
                                <button
                                  type="button"
                                  disabled={isIpdPharmacyOrLab}
                                  onClick={() =>
                                    !isIpdPharmacyOrLab &&
                                    setSelectedPayment(payment)
                                  }
                                  className={`px-3 py-2 text-sm font-semibold rounded-md shadow-sm ${
                                    isIpdPharmacyOrLab
                                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                      : "bg-primary text-white hover:bg-hover"
                                  }`}
                                >
                                  Collect Payment
                                </button>
                              );
                            })()
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                payment.invoiceId &&
                                setSelectedBillPayment(payment)
                              }
                              disabled={!payment.invoiceId}
                              className="px-3 py-2 text-sm font-semibold text-primary border border-primary rounded-md hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
                            >
                              View Bill
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-10 text-sm text-center text-gray-500"
                      >
                        {displayLoading
                          ? `Loading ${activeTab} payments...`
                          : `No ${activeTab} payments found.`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {activeTab === "completed" &&
              completedPagination.total > completedPagination.perPage && (
                <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                  <div className="text-sm text-gray-700">
                    Showing {(completedPagination.page - 1) * completedPagination.perPage + 1} to{" "}
                    {Math.min(
                      completedPagination.page * completedPagination.perPage,
                      completedPagination.total
                    )}{" "}
                    of {completedPagination.total} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        fetchCompletedPayments(completedPagination.page - 1)
                      }
                      disabled={completedPagination.page <= 1}
                      className="px-3 py-1 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        fetchCompletedPayments(completedPagination.page + 1)
                      }
                      disabled={
                        completedPagination.page * completedPagination.perPage >=
                        completedPagination.total
                      }
                      className="px-3 py-1 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {selectedPayment && (
        <PaymentModal
          isOpen={!!selectedPayment}
          onClose={() => setSelectedPayment(null)}
          total={selectedPayment.amount}
          discount={0}
          patientId={selectedPayment.patientId ?? 0}
          patientName={selectedPayment.patientName}
          sourceType={getPaymentModalSourceType(selectedPayment.typeCode)}
          sourceId={selectedPayment.sourceId}
          onPaymentSuccess={() => {
            setSelectedPayment(null);
            if (activeTab === "pending") fetchPendingPayments();
          }}
        />
      )}

      {selectedBillPayment && (
        <ReceiptModal
          isOpen={!!selectedBillPayment}
          onClose={() => setSelectedBillPayment(null)}
          invoiceId={selectedBillPayment.invoiceId}
          receiptType={
            selectedBillPayment.typeCode === "lab_order"
              ? "pathology"
              : selectedBillPayment.typeCode
          }
        />
      )}

    </div>
  );
};

export default BillingList;
